import {
  ACTIVE_STATUSES,
  BANK_ACCOUNT_TYPE,
  NRI_BANK_ACCOUNT_TYPE,
  HOLDING_TYPE,
  JOINT_CLIENT_HOLDING_PATTERNS,
  MINOR_NEW_GUARDIAN_ONBOARDING_STEPS,
  MINOR_ONBOARDING_STEPS,
  NRI_ONBOARDING_STEPS,
  NRI_TAX_STATUSES,
  ONBOARDING_STEPS,
} from "../constants";
import {
  Client,
  ClientHoldingPattern,
  ClientStatus,
  OnboardingChecklistItem,
} from "../types";

export function isActiveClient<T extends { status: ClientStatus }>(client?: T) {
  if (!client) return false;
  return ACTIVE_STATUSES.includes(client.status);
}

export function hasCresoAccount(client?: Client) {
  if (!client) return false;
  if (isActiveClient(client)) return true;
  return (
    client?.status === "SETUP_PENDING" || client?.status === "UNDER_REVIEW"
  );
}

export function isNRI(client?: Client) {
  if (!client) return false;
  return NRI_TAX_STATUSES.has(client.tax_status?.toLocaleLowerCase() ?? "");
}

// Maps a client's tax status onto the Account type options (ACCOUNT_TYPE_OPTIONS)
export function getAccountType(client?: Client) {
  if (isNRI(client)) return "nri";
  if (client?.tax_status?.toLocaleLowerCase().includes("minor")) {
    return "on_behalf_of_minor";
  }
  return "resident_individual";
}

export function isMinor(client?: Client | null) {
  return getAccountType(client ?? undefined) === "on_behalf_of_minor";
}

/**
 * `newGuardian`: a minor whose guardian isn't your client yet gets an extra
 * Guardian profile step.
 */
export function getOnboardingSteps(
  client?: Client | null,
  { newGuardian = false }: { newGuardian?: boolean } = {}
) {
  if (isNRI(client ?? undefined)) return NRI_ONBOARDING_STEPS;
  if (isMinor(client)) {
    return newGuardian
      ? MINOR_NEW_GUARDIAN_ONBOARDING_STEPS
      : MINOR_ONBOARDING_STEPS;
  }
  return ONBOARDING_STEPS;
}

/**
 * Bank account types a client can add. NRIs get NRE / NRO, and once the
 * account is registered only the type their tax status was set to.
 */
export function getBankAccountTypeOptions(client?: Client | null) {
  const taxStatus = client?.tax_status?.toLocaleLowerCase();
  if (taxStatus === "nri_repatriable_nre") {
    return NRI_BANK_ACCOUNT_TYPE.filter((t) => t.value === "NE");
  }
  if (taxStatus === "nri_repatriable_nro") {
    return NRI_BANK_ACCOUNT_TYPE.filter((t) => t.value === "NO");
  }
  if (taxStatus === "nri") return NRI_BANK_ACCOUNT_TYPE;
  return BANK_ACCOUNT_TYPE;
}

type ErrorBody = {
  code?: string;
  details?: { missing?: OnboardingChecklistItem[] };
};

/**
 * What's left when activation fails with `ONBOARDING_INCOMPLETE`, else null.
 * Reads web's APIError (`data`) and the apps' raw error body (`error`).
 */
export function getOnboardingIncompleteItems(
  error: unknown
): OnboardingChecklistItem[] | null {
  const { data, error: body } = (error ?? {}) as {
    data?: ErrorBody;
    error?: ErrorBody;
  };
  const errorBody = data ?? body;
  if (errorBody?.code !== "ONBOARDING_INCOMPLETE") return null;
  return errorBody.details?.missing ?? [];
}

/** Savings, Current, NRE or NRO */
export function getBankAccountTypeLabel(type?: string | null) {
  return [...BANK_ACCOUNT_TYPE, ...NRI_BANK_ACCOUNT_TYPE].find(
    (t) => t.value === type
  )?.label;
}

type TaxStatusFields = {
  tax_status?: string | null;
  tax_status_display?: string | null;
};

export function getTaxStatusLabel(client?: TaxStatusFields | null) {
  const taxStatus = client?.tax_status?.toLocaleLowerCase();
  if (!taxStatus) return "Individual";
  if (taxStatus === "resident_individual") return "Individual";
  if (taxStatus.includes("nri")) return "NRI";
  if (taxStatus.includes("minor")) return "Minor";
  if (taxStatus.includes("huf")) return "HUF";
  return "Individual";
}

// Only minor and NRI clients get a tax status badge
export function getTaxStatusBadgeLabel(client?: TaxStatusFields | null) {
  const label = getTaxStatusLabel(client);
  if (label !== "NRI" && label !== "Minor") return null;
  return client?.tax_status_display || label;
}

export function enableUCCModification(client?: Client) {
  if (client?.tax_status !== "resident_individual") {
    return false;
  }
  if (client?.status === "AUTHENTICATION_PENDING") {
    return false;
  }
  return true;
}

// The "Joint" holding filter covers both joint and anyone_or_survivor
// accounts: its count adds both, and selecting it filters by both.
export function getHoldingPatternFilterCount(
  counts: Partial<Record<ClientHoldingPattern, number>> | undefined,
  value: string
) {
  if (value === "joint") {
    return JOINT_CLIENT_HOLDING_PATTERNS.reduce(
      (sum, pattern) => sum + (counts?.[pattern] ?? 0),
      0
    );
  }
  return counts?.[value as ClientHoldingPattern] ?? 0;
}

// Saved filters from older sessions may hold "anyone_or_survivor" for the
// Joint option, so map them onto the current options.
export function normalizeHoldingPatternFilter(selected: string[] = []) {
  const options = HOLDING_TYPE.map((ht) => ht.value);
  return [
    ...new Set(
      selected.map((value) =>
        JOINT_CLIENT_HOLDING_PATTERNS.includes(value as ClientHoldingPattern)
          ? "joint"
          : value
      )
    ),
  ].filter((value) => options.includes(value));
}

export function getHoldingPatternFilterValues(selected: string[] = []) {
  return [
    ...new Set(
      selected.flatMap((value) =>
        value === "joint" ? JOINT_CLIENT_HOLDING_PATTERNS : [value]
      )
    ),
  ];
}
